
import React, { useState } from 'react';
import { Plus, Clock, Upload } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { ScheduleMessageModal } from '@/components/scheduled-messages/ScheduleMessageModal';
import { ImportContactsModal } from './ImportContactsModal';
import { useFeatureBlocking } from '@/hooks/useFeatureBlocking';
import { RestrictedFeatureWrapper } from '@/components/ui/RestrictedFeatureWrapper';

/**
 * Cabeçalho da página de Contatos - ATUALIZADO COM IMPORTAÇÃO
 * Exibe o título da página e os botões de ação.
 * 
 * Props:
 * - onAddLead: Função a ser chamada ao clicar no botão "Adicionar Lead".
 * 
 * Onde é usado:
 * - ClientsPage.tsx
 * 
 * MUDANÇAS:
 * - Adicionado botão "Importar Contatos" que abre modal de importação
 * - Mantido botão "Programar Mensagem" existente
 * - UI verde seguindo design system
 */
interface ClientsPageHeaderProps {
  onAddLead: () => void;
}

export const ClientsPageHeader: React.FC<ClientsPageHeaderProps> = ({
  onAddLead
}) => {
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const { canImportContacts, canScheduleMessages } = useFeatureBlocking();

  return (
    <>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contatos</h1>
          <p className="text-gray-600 mt-1">Gerencie sua base de contatos e leads</p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Botão Importar - Restrito ao plano Pro+ */}
          <RestrictedFeatureWrapper
            feature="canImportContacts"
            featureName="importação de contatos"
            requiredPlan="Pro"
            fallback={
              <Button
                variant="outline"
                disabled
                className="border-gray-300 text-gray-400 rounded-xl cursor-not-allowed text-xs sm:text-sm h-9 sm:h-10"
                title="Disponível no plano Pro"
              >
                <Upload className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Importar</span>
                <span className="sm:hidden">Importar</span> (Pro)
              </Button>
            }
          >
            <Button
              onClick={() => setIsImportModalOpen(true)}
              variant="outline"
              className="border-green-500 shadow-sm hover:shadow-md transition-all duration-200 bg-green-50 text-green-700 rounded-xl text-xs sm:text-sm h-9 sm:h-10"
            >
              <Upload className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
              Importar <span className="hidden sm:inline ml-1">Contatos</span>
            </Button>
          </RestrictedFeatureWrapper>

          {/* Botão Programar Mensagem - Restrito ao plano Pro+ */}
          <RestrictedFeatureWrapper
            feature="canScheduleMessages"
            featureName="disparo programado"
            requiredPlan="Pro"
            fallback={
              <Button
                variant="outline"
                disabled
                className="border-gray-300 text-gray-400 rounded-xl cursor-not-allowed text-xs sm:text-sm h-9 sm:h-10"
                title="Disponível no plano Pro"
              >
                <Clock className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Programar</span> (Pro)
              </Button>
            }
          >
            <Button
              onClick={() => setIsScheduleModalOpen(true)}
              variant="outline"
              className="border-green-500 shadow-sm hover:shadow-md transition-all duration-200 bg-green-50 text-green-700 rounded-xl text-xs sm:text-sm h-9 sm:h-10"
            >
              <Clock className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Programar</span>
              <span className="sm:hidden">Programar</span>
            </Button>
          </RestrictedFeatureWrapper>

          <Button
            onClick={onAddLead}
            className="bg-green-600 hover:bg-green-700 shadow-sm hover:shadow-md transition-all duration-200 rounded-xl text-xs sm:text-sm h-9 sm:h-10"
          >
            <Plus className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
            Add Lead
          </Button>
        </div>
      </div>

      {/* Modais - só mostrar se tiver permissão */}
      {canScheduleMessages && (
        <ScheduleMessageModal
          isOpen={isScheduleModalOpen}
          onClose={() => setIsScheduleModalOpen(false)}
          selectedLead={null}
        />
      )}

      {canImportContacts && (
        <ImportContactsModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
        />
      )}
    </>
  );
};
